import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Handle, Position, useReactFlow, useStore } from '@xyflow/react';
import { nanoid } from 'nanoid';
import { toast } from 'react-toastify';
import ApiService from '../../../api/ApiService';
import { useProject } from '../providers/ProjectProvider';
import { useNodeOperations } from '../providers/NodeOperationsProvider';
import { TaskPricePreview } from './TaskPricePreview';
import { useTaskQuote } from './useTaskQuote';
import './ModernImageModelNode.css';

const apiService = new ApiService();

const OUTPUT_FORMATS = ['png', 'jpeg', 'webp'];
const RESOLUTIONS = ['1K', '2K', '4K'];
const MISSING_PROMPT_ERROR = '请先连接文本输入节点并填写提示词';

const normalizeImage = (image) => {
  if (typeof image === 'string') return image;
  return image?.url || image?.image_url || null;
};

export const ModernImageModelNode = memo(({ id, data, config }) => {
  const { updateNodeData, getNode, getEdges } = useReactFlow();
  const edges = useStore(state => state.edges);
  const nodes = useStore(state => state.nodes);
  const { readOnly } = useProject();
  const { addNodesWithEdges, duplicateNode, createNodeAndConnect } = useNodeOperations();

  const [aspectRatio, setAspectRatio] = useState(data.aspectRatio || config.defaultAspectRatio);
  const [resolution, setResolution] = useState(data.resolution || '1K');
  const [outputFormat, setOutputFormat] = useState(data.outputFormat || 'png');
  const [quality, setQuality] = useState(data.quality || config.defaultQuality || 'high');
  const [numImages, setNumImages] = useState(data.numImages || 1);
  const [enableWebSearch, setEnableWebSearch] = useState(Boolean(data.enableWebSearch));
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const fixedNumImages = config.fixedNumImages == null ? null : Number(config.fixedNumImages);
  const effectiveNumImages = fixedNumImages ?? Number(numImages);
  const canSelectOutputFormat = config.supportsOutputFormatSelection !== false;
  const effectiveOutputFormat = canSelectOutputFormat
    ? outputFormat
    : (config.defaultOutputFormat || 'png');

  useEffect(() => {
    updateNodeData(id, {
      aspectRatio,
      resolution,
      outputFormat: effectiveOutputFormat,
      ...(config.supportsQuality ? { quality } : {}),
      ...(config.supportsNumImages ? { numImages: effectiveNumImages } : {}),
      ...(config.supportsWebSearch ? { enableWebSearch } : {}),
    });
  }, [
    id,
    aspectRatio,
    resolution,
    effectiveOutputFormat,
    quality,
    effectiveNumImages,
    enableWebSearch,
    config,
    updateNodeData,
  ]);

  const connectedPrompt = useMemo(() => {
    return edges
      .filter(edge => edge.target === id && edge.targetHandle === 'prompt')
      .map(edge => nodes.find(node => node.id === edge.source))
      .filter(node => node?.type === 'promptInput' && node.data?.prompt?.trim())
      .map(node => node.data.prompt.trim())
      .join('\n');
  }, [edges, nodes, id]);

  const connectedImages = useMemo(() => {
    const images = [];
    edges
      .filter(edge => edge.target === id && edge.targetHandle === 'image')
      .forEach(edge => {
        const sourceNode = nodes.find(node => node.id === edge.source);
        if (sourceNode?.type === 'imageReference' || sourceNode?.type === 'imageDisplay') {
          images.push(sourceNode.data?.imageUrl);
        } else if (sourceNode?.type === 'drawing') {
          const imageUrl = sourceNode.data?.imageUrl;
          if (imageUrl && !imageUrl.startsWith('data:') && !imageUrl.startsWith('blob:')) {
            images.push(imageUrl);
          }
        } else if (sourceNode?.type === 'imageTask' && sourceNode.data?.status === 'completed') {
          (sourceNode.data.generatedImages || []).forEach(image => images.push(normalizeImage(image)));
        }
      });
    return [...new Set(images.filter(Boolean))];
  }, [edges, nodes, id]);

  useEffect(() => {
    if (connectedPrompt && error === MISSING_PROMPT_ERROR) {
      setError(null);
    }
  }, [connectedPrompt, error]);

  const isEditMode = connectedImages.length > 0;
  const aspectRatioOptions = isEditMode && config.editAspectRatios
    ? config.editAspectRatios
    : config.aspectRatios;

  const inputParams = useMemo(() => ({
    prompt: connectedPrompt,
    [config.ratioParam]: aspectRatio,
    resolution,
    output_format: effectiveOutputFormat,
    ...(config.supportsQuality ? { quality, num_images: 1 } : {}),
    ...(config.supportsNumImages ? { num_images: effectiveNumImages } : {}),
    ...(config.supportsWebSearch ? { enable_web_search: enableWebSearch } : {}),
    ...(isEditMode ? { image_urls: connectedImages } : {}),
  }), [
    aspectRatio,
    config.ratioParam,
    config.supportsNumImages,
    config.supportsQuality,
    config.supportsWebSearch,
    connectedImages,
    connectedPrompt,
    effectiveNumImages,
    effectiveOutputFormat,
    enableWebSearch,
    isEditMode,
    quality,
    resolution,
  ]);

  const taskQuote = useTaskQuote({
    enabled: Boolean(connectedPrompt) && !readOnly,
    apiVersion: 'v3',
    model: config.modelId,
    params: inputParams,
  });

  useEffect(() => {
    if (!aspectRatioOptions.some(option => option.value === aspectRatio)) {
      setAspectRatio(config.defaultAspectRatio);
    }
  }, [aspectRatio, aspectRatioOptions, config.defaultAspectRatio]);

  const isValidConnection = useCallback((connection) => {
    const sourceNode = getNode(connection.source);
    if (connection.targetHandle === 'prompt') {
      return sourceNode?.type === 'promptInput';
    }
    if (connection.targetHandle === 'image') {
      if (!['imageReference', 'drawing', 'imageTask', 'imageDisplay'].includes(sourceNode?.type)) {
        return false;
      }
      const imageEdgeCount = getEdges().filter(
        edge => edge.target === id && edge.targetHandle === 'image'
      ).length;
      if (imageEdgeCount >= config.imageLimit) {
        toast.warning(`最多支持 ${config.imageLimit} 张参考图`);
        return false;
      }
    }
    return true;
  }, [config.imageLimit, getEdges, getNode, id]);

  const handleCreateInput = useCallback((event, handleId, handleType) => {
    event.stopPropagation();
    if (readOnly) {
      toast.info('只读模式下无法创建节点');
      return;
    }
    createNodeAndConnect(id, config.type, handleId, handleType);
  }, [config.type, createNodeAndConnect, id, readOnly]);

  const handleGenerate = async () => {
    try {
      setError(null);
      setIsGenerating(true);
      if (!connectedPrompt) throw new Error(MISSING_PROMPT_ERROR);
      if (connectedImages.length > config.imageLimit) {
        throw new Error(`参考图不能超过 ${config.imageLimit} 张`);
      }

      const confirmedQuote = await taskQuote.quoteNow();
      const response = await apiService.createV3Task(config.modelId, inputParams);
      const taskId = response?.data?.task_id;
      if (!taskId) throw new Error(response?.message || '创建图片任务失败');

      const currentNode = getNode(id);
      const taskNodeId = nanoid();
      await addNodesWithEdges([
        {
          id: taskNodeId,
          type: 'imageTask',
          position: {
            x: currentNode.position.x + 440,
            y: currentNode.position.y,
          },
          data: {
            taskId,
            taskType: config.taskType,
            apiTaskType: 'v3_api',
            modelId: config.modelId,
            status: 'pending',
            inputParams,
            chargedPoints: Number(response?.data?.price ?? confirmedQuote.points),
            balanceAfterCharge: response?.data?.balance,
            generatedImages: [],
          },
        },
      ], [
        {
          id: nanoid(),
          source: id,
          target: taskNodeId,
          type: 'default',
        },
      ]);

      toast.success(`${config.title} 任务已创建${isEditMode ? `，使用 ${connectedImages.length} 张参考图` : ''}`);
    } catch (generationError) {
      console.error(`${config.title} 图片生成失败:`, generationError);
      const message = generationError?.message || '生成失败，请重试';
      setError(message);
      toast.error(message);
    } finally {
      setIsGenerating(false);
    }
  };

  const quoteBlocked = Boolean(connectedPrompt) && taskQuote.status !== 'success';
  const disabled = isGenerating || readOnly;
  const submitDisabled = disabled || quoteBlocked;

  return (
    <div
      className="ModernImageModelNode-container"
      style={{ '--model-accent': config.accent, '--model-soft': config.soft }}
    >
      <button
        type="button"
        className="ModernImageModelNode-duplicate"
        onClick={event => {
          event.stopPropagation();
          duplicateNode(id);
        }}
        title="复制节点"
        aria-label={`复制 ${config.title} 节点`}
      >
        <i className="bi bi-copy" />
      </button>

      <Handle
        type="target"
        position={Position.Left}
        id="prompt"
        className="ModernImageModelNode-handle ModernImageModelNode-handle-prompt"
        style={{ top: '29%' }}
        isValidConnection={isValidConnection}
      >
        <button
          type="button"
          className="ModernImageModelNode-handle-label"
          onClick={event => handleCreateInput(event, 'prompt', 'text')}
          title="创建文本输入节点"
        >
          文本 +
        </button>
      </Handle>

      <Handle
        type="target"
        position={Position.Left}
        id="image"
        className="ModernImageModelNode-handle ModernImageModelNode-handle-image"
        style={{ top: '69%' }}
        isValidConnection={isValidConnection}
      >
        <button
          type="button"
          className="ModernImageModelNode-handle-label"
          onClick={event => handleCreateInput(event, 'image', 'image')}
          title={`创建图片参考节点，最多 ${config.imageLimit} 张`}
        >
          参考图 +
        </button>
      </Handle>

      <Handle
        type="source"
        position={Position.Right}
        className="ModernImageModelNode-handle ModernImageModelNode-handle-output"
      />

      <div className="ModernImageModelNode-header">
        <div className="ModernImageModelNode-brand">
          <span className="ModernImageModelNode-icon">{config.icon}</span>
          <div>
            <div className="ModernImageModelNode-title">{config.title}</div>
            <div className="ModernImageModelNode-model-id">{config.modelLabel}</div>
          </div>
        </div>
        <span className="ModernImageModelNode-mode">{isEditMode ? '参考图编辑' : '文字生成'}</span>
      </div>

      <div className="ModernImageModelNode-body">
        <div className="ModernImageModelNode-connection-row">
          <span className={connectedPrompt ? 'is-connected' : ''}>
            <i className={`bi ${connectedPrompt ? 'bi-check-circle-fill' : 'bi-circle'}`} />
            提示词
          </span>
          <span className={isEditMode ? 'is-connected' : ''}>
            <i className={`bi ${isEditMode ? 'bi-images' : 'bi-image'}`} />
            {isEditMode ? `${connectedImages.length} 张参考图` : '参考图可选'}
          </span>
        </div>

        <label className="ModernImageModelNode-field">
          <span>画面比例</span>
          <select value={aspectRatio} onChange={event => setAspectRatio(event.target.value)} disabled={disabled}>
            {aspectRatioOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>

        <label className="ModernImageModelNode-field">
          <span>分辨率</span>
          <select value={resolution} onChange={event => setResolution(event.target.value)} disabled={disabled}>
            {RESOLUTIONS.map(value => <option key={value} value={value}>{value}</option>)}
          </select>
        </label>

        {config.supportsQuality && (
          <div className="ModernImageModelNode-field">
            <span>生成质量</span>
            <div
              className="ModernImageModelNode-segments nodrag nopan"
              onPointerDown={event => event.stopPropagation()}
            >
              {[
                ['low', '快速'],
                ['medium', '标准'],
                ['high', '精细'],
              ].map(([value, label]) => (
                <button
                  type="button"
                  key={value}
                  className={quality === value ? 'is-active' : ''}
                  onClick={() => setQuality(value)}
                  aria-pressed={quality === value}
                  disabled={disabled}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        {config.supportsNumImages && config.showNumImagesControl !== false && (
          <label className="ModernImageModelNode-field">
            <span>生成数量</span>
            <select
              value={effectiveNumImages}
              onChange={event => setNumImages(Number(event.target.value))}
              disabled={disabled || fixedNumImages != null}
            >
              {(config.numImageOptions || [1, 2, 3, 4]).map(value => (
                <option key={value} value={value}>{value} 张</option>
              ))}
            </select>
          </label>
        )}

        {canSelectOutputFormat && (
          <div className="ModernImageModelNode-field">
            <span>输出格式</span>
            <div
              className="ModernImageModelNode-segments nodrag nopan"
              onPointerDown={event => event.stopPropagation()}
            >
              {OUTPUT_FORMATS.map(value => (
                <button
                  type="button"
                  key={value}
                  className={outputFormat === value ? 'is-active' : ''}
                  onClick={() => setOutputFormat(value)}
                  aria-pressed={outputFormat === value}
                  disabled={disabled}
                >
                  {value.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        )}

        {config.supportsWebSearch && (
          <label className="ModernImageModelNode-toggle">
            <input
              type="checkbox"
              checked={enableWebSearch}
              onChange={event => setEnableWebSearch(event.target.checked)}
              disabled={disabled}
            />
            <span>联网增强提示词</span>
            <small>适合实时人物、地点与商品</small>
          </label>
        )}

        <TaskPricePreview quote={taskQuote} />

        {error && <div className="ModernImageModelNode-error">{error}</div>}

        <button
          type="button"
          className="ModernImageModelNode-generate"
          onClick={handleGenerate}
          disabled={submitDisabled}
        >
          {isGenerating ? (
            <><span className="ModernImageModelNode-spinner" />正在创建任务</>
          ) : (
            <><i className="bi bi-stars" />{isEditMode ? '开始编辑' : '创建图片任务'}</>
          )}
        </button>
      </div>
    </div>
  );
});
